import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
// Pre-existing circular import (redux-core/serviceHelpers <-> redux-core/rights/services.ts,
// reached via utils/request -> redux-core/auth/actions -> ... -> redux-core/rights/services.ts):
// when serviceHelpers is the first module in a require graph to reach that cycle, the
// re-entrant serviceHelpers module is still mid-evaluation and rights/services.ts's top-level
// makeEntityServiceCalls(...) call crashes. Importing utils/request directly first (as the
// sibling import-tracks-spreadsheet/actions.js does incidentally via genericGetCallRequest)
// resolves that module ahead of time so the cycle closes safely. Unrelated to this feature.
import 'utils/request';
import { makeServiceCall } from 'redux-core/serviceHelpers';
import { FileError, MIN_ROWS, MAX_ROWS, REQUIRED_COLUMNS, ValidationResult } from './constants';

const TEMPLATE_PATH = '/templates/commercial_tracks_import_template.xlsx';
const TEMPLATE_FILENAME = 'commercial_tracks_import_template.xlsx';
const SHEET_NAME = 'Sheet1';

export const getCommercialTemplateFile = async (): Promise<void> => {
  const response = await fetch(TEMPLATE_PATH);
  if (!response.ok) throw new Error('Template download failed');
  const blob = await response.blob();
  FileSaver.saveAs(blob, TEMPLATE_FILENAME);
};

const collapseWhitespace = (value: string): string => value.trim().replace(/\s+/g, ' ');

const headerKey = (cell: unknown): string =>
  collapseWhitespace(String(cell ?? ''))
    .replace(/\s*\([^()]*\)$/, '')
    .toLowerCase();

const readFileBytes = (file: File): Promise<ArrayBuffer> => {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

// Client-side pre-flight validation (SheetJS parse). This is the client half of the
// split-validation design: it catches obvious spreadsheet problems immediately in the
// browser, while the import engine's readFile step revalidates the same rules server-side
// before processing. Keep this parser in sync with the qwire-import commercial-import parser.
const isZipSignature = (bytes: ArrayBuffer): boolean => {
  const view = new Uint8Array(bytes);
  return view.length >= 2 && view[0] === 0x50 && view[1] === 0x4b;
};

export const validateCommercialImportFile = async ({ file }: { file: File }): Promise<ValidationResult> => {
  let workbook: XLSX.WorkBook;
  try {
    const bytes = await readFileBytes(file);
    if (!isZipSignature(bytes)) {
      return { ok: false, errors: [{ code: 'FILE_STRUCTURE_INVALID' }] };
    }
    workbook = XLSX.read(bytes, { type: 'array' });
  } catch (error) {
    return { ok: false, errors: [{ code: 'FILE_STRUCTURE_INVALID' }] };
  }

  const worksheet = workbook.Sheets[SHEET_NAME];
  if (!worksheet) {
    return { ok: false, errors: [{ code: 'FILE_STRUCTURE_INVALID' }] };
  }

  const grid: unknown[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
  const headerRow = grid[0] ?? [];
  const columnIndex = new Map<string, number>();
  headerRow.forEach((cell, index) => {
    const key = headerKey(cell);
    REQUIRED_COLUMNS.forEach((column) => {
      if (key === column.toLowerCase() && !columnIndex.has(column)) {
        columnIndex.set(column, index);
      }
    });
  });

  const missingHeaders = REQUIRED_COLUMNS.filter((column) => !columnIndex.has(column));
  if (missingHeaders.length > 0) {
    return { ok: false, errors: [{ code: 'FILE_STRUCTURE_INVALID', params: { missingHeaders } }] };
  }

  const cellString = (value: unknown): string => (value == null ? '' : String(value));
  const cellAt = (row: unknown[], column: string): string => cellString(row[columnIndex.get(column) as number]);

  const missingTitleLines: number[] = [];
  let dataRowCount = 0;

  grid.slice(1).forEach((row, index) => {
    const line = index + 2;
    const isBlank = REQUIRED_COLUMNS.every((column) => collapseWhitespace(cellAt(row, column)) === '');
    if (isBlank) return;
    dataRowCount += 1;

    const trackTitle = collapseWhitespace(cellAt(row, 'Track Title'));
    if (trackTitle === '') {
      missingTitleLines.push(line);
    }
  });

  const errors: FileError[] = [];
  if (missingTitleLines.length > 0) {
    errors.push({ code: 'MISSING_REQUIRED_INFO', params: { lines: missingTitleLines } });
  }
  if (dataRowCount < MIN_ROWS) {
    errors.push({ code: 'TOO_FEW_ROWS', params: { count: dataRowCount, min: MIN_ROWS } });
  } else if (dataRowCount > MAX_ROWS) {
    errors.push({ code: 'TOO_MANY_ROWS', params: { count: dataRowCount, max: MAX_ROWS } });
  }

  return { ok: errors.length === 0, errors };
};

// makeServiceCall/postCallAPI resolve to the untyped JS request layer, whose static return
// type (AxiosResponse) doesn't reflect the response-interceptor's runtime data-unwrap — so the
// resolved shape below is left to the caller rather than asserted here.
export const getCommercialImportUploadAccessCall = async ({
  kind,
  contentType,
}: {
  kind: string;
  contentType: string;
}) => {
  const call = makeServiceCall('uploads', 'upload-commercial-import-file');
  return call({ kind, contentType });
};

export const startCommercialImportCall = async ({
  productionId,
  userId,
  s3FileKey,
}: {
  productionId: string | number;
  userId: string | number;
  s3FileKey: string;
}) => {
  // divisionId is server-derived and must NOT be sent from the client.
  const call = makeServiceCall('commercial-import', 'start', 'clear');
  return call({ productionId, userId, s3FileKey });
};
